import React, { useCallback, useEffect, useState } from 'react';
import { FEATURE_REQUEST_HANDLER } from '../../../../../core/constants/feedback';
import { matchPattern } from '../../../../../core/utils/string';
import {
  TRACKER_SELECT_TARGET_TYPE,
  TRACKER_SUGGEST_TARGET_CLICK,
  TRACKER_TARGET_TYPE_EMPTY_RESULT
} from '../../../constants';
import { TARGET_TYPES } from '../../../models/target-type';
import { TargetTypeInfo } from '../../../models/target-type-info';
import { TargetTypesFactory } from '../../../utils';
import { NoEntityTypeAlert } from '../../../../../fallback-screens/components/no-entity-type-alert/NoEntityTypeAlert';
import RetryApiAlert from '../../../../../fallback-screens/components/retry-api-alert/RetryApiAlert';
import SearchArea from '../../../../../list/components/search-area/SearchArea';
import StickySearchWrapper from '../../../../../list/components/sticky-search-wrapper/StickySearchWrapper';
import { TileGrid } from '@hevoio/hevo-ui-toolkit';
import { DEFAULT_TILE_HEIGHT } from '../../../../../layout/constants/tileGrid';
import EntityTile from '../../../../../layout/components/entity-tile/EntityTile';
import { TileSkeletonGrid } from '@hevoio/hevo-ui-toolkit';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import useEmptySearchResultTrackerService from '../../../../../list/hooks/useEmptySearchResultTrackerService';
import ActivateTargetAPI from '../../../ActivateTargetAPI';
import TargetTypeTilesFactory from './tiles-factory';
import { HdPane } from '../../../../../components';
import useService from '../../../../../injector/hooks/useService';
import { ActivationStatusService } from '../../../../../core/services/activation-status.service';
import { HdTimeFormat, timeFormatter } from '../../../../../date-time/components/calendar-format/HdCalendar';

const DEBOUNCE_INTERVAL = 500;

interface SearchableListProps {
  onTypeSelect: Function;
  saveSearch: Function;
  defaultSearch?: string;
  scrollElement?: HTMLElement | null;
}

export default function SelectTargetTypeList({
  onTypeSelect,
  saveSearch,
  defaultSearch,
  scrollElement
}: SearchableListProps) {
  const [allTargetTypes, setAllTargetTypes] = useState([]);
  const [filteredTargetTypes, setFilteredTargetTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState(defaultSearch);
  const [apiError, setApiError] = useState(null);
  const [loading, setLoading] = useState(true);
  const analyticsTracker = useAnalyticsTracker();
  const emptyResultTracker = useEmptySearchResultTrackerService();
  const _activateStatusService = useService(ActivationStatusService);

  const showTargetTypes = allTargetTypes?.length;
  const showPageError = !!apiError;
  const showNoMatchAlert = filteredTargetTypes.length === 0 && !loading && !apiError;

  const suggestionContext = { name: 'Target', value: 'TARGET' };

  const activateShutDownDate = _activateStatusService.getActivationSunsetEpoch();
  const showActivateBanner = _activateStatusService.shouldShowActivationBanner();

  useEffect(() => {
    getPageData();
  }, []);

  const getPageData = () => {
    if (apiError) {
      setApiError(null);
    }

    setLoading(true);

    ActivateTargetAPI.getTargetTypes()
      .then(({ data }) => {
        const targetTypeTiles = TargetTypeTilesFactory(
          TargetTypesFactory(data).filter(targetType => !TARGET_TYPES[targetType.type]?.isHidden)
        ).map(targetTile => {
          const targetTileClone = { ...targetTile };
          const { isCrmTarget } = TARGET_TYPES[targetTile.id];

          if (!isCrmTarget && showActivateBanner) {
            targetTileClone.metaData = { ...targetTile.metaData };
            targetTileClone.metaData.badgeMetaData = {
              ...targetTile.metaData.badgeMetaData,
              showDeprecatingBadge: true,
              deprecatingBadgeTooltip: `${targetTile.title} will be Deprecated from ${timeFormatter(
                activateShutDownDate
              )}`
            };
          }

          return targetTileClone;
        });
        setAllTargetTypes(targetTypeTiles);

        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        setApiError(error);
      });
  };

  const onSearch = useCallback(
    (searchInput: string) => {
      saveSearch(searchInput);
      setSearchTerm(searchInput);

      if (searchInput.trim().length === 0) {
        setFilteredTargetTypes(allTargetTypes);
        return;
      }

      const searchedTargetTypes = allTargetTypes.filter(targetTypeTile =>
        matchPattern([targetTypeTile.data.typeDisplay], searchInput)
      );

      setFilteredTargetTypes(searchedTargetTypes);

      if (searchedTargetTypes.length === 0) {
        emptyResultTracker.onEmptyResult(TRACKER_TARGET_TYPE_EMPTY_RESULT, searchInput, {
          searchTerm: searchInput,
          searchResultEmpty: true
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allTargetTypes]
  );

  const getSearchAnalyticsData = () => ({
    searchTerm,
    searchResultEmpty: filteredTargetTypes.length === 0
  });

  const onSuggestTargetTypeClick = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_SUGGEST_TARGET_CLICK,
      properties: {
        handler: FEATURE_REQUEST_HANDLER,
        ...getSearchAnalyticsData()
      }
    });
  };

  const onTargetTypeClick = (targetType: TargetTypeInfo) => {
    analyticsTracker.eventTrack({
      action: TRACKER_SELECT_TARGET_TYPE,
      properties: {
        targetType: targetType.type,
        searchTerm
      }
    });

    onTypeSelect(targetType);
  };

  const dataId = 'target-type-list';

  return (
    <>
      <StickySearchWrapper>
        <SearchArea
          dataId={dataId}
          autofocus
          onSearch={onSearch}
          debounceInterval={DEBOUNCE_INTERVAL}
          placeholder='Search Target Types'
          collapsible={false}
          defaultSearch={defaultSearch}
        />
      </StickySearchWrapper>

      {showActivateBanner && (
        <div className='d-flex justify-center mt-7'>
          <HdPane icon='deprecate' variant='warning-faded' className='w-75 mb-0 px-4'>
            <span className='text-bold'>Important update</span>: Effective{' '}
            <HdTimeFormat value={activateShutDownDate} />, Activate will be available only for
            Salesforce and HubSpot Targets. For more information, please contact Hevo Support or
            your account executive.
          </HdPane>
        </div>
      )}

      <div className='node-container'>
        {showTargetTypes ? (
          <TileGrid
            tileHeight={DEFAULT_TILE_HEIGHT}
            tiles={filteredTargetTypes}
            onTileClick={onTargetTypeClick}
            TileComponent={EntityTile}
            scrollElement={scrollElement}
          />
        ) : null}

        {showNoMatchAlert ? (
          <NoEntityTypeAlert
            suggestionContext={suggestionContext}
            actionHandler={onSuggestTargetTypeClick}
          />
        ) : null}

        {loading ? <TileSkeletonGrid tileCount={4} /> : null}

        {showPageError ? (
          <RetryApiAlert actionHandler={getPageData} error={apiError} dataId={dataId} />
        ) : null}
      </div>
    </>
  );
}
